import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  auth,
  createUserWithEmailAndPassword,
  updateProfile,
  sendEmailVerification,
  authActionSettings,
} from '../firebase.js';
import { Me, Public } from '../api.js';
import { useAuth } from '../AuthContext.jsx';
import { FormField, Switch } from '../components/FormField.jsx';
import { DynamicField, validateCustomFieldsClient } from '../components/DynamicField.jsx';
import { checkPassword, policySummary } from '../utils/passwordPolicy.js';

export default function Signup() {
  const nav = useNavigate();
  const { refreshRole } = useAuth();
  const [form, setForm] = useState({
    email: '',
    password: '',
    displayName: '',
    phone: '',
    marketingOptIn: true,
  });
  const [customFields, setCustomFields] = useState({});
  const [customDefs, setCustomDefs] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Public.signupFields().then((r) => setCustomDefs(r.fields)).catch(() => setCustomDefs([]));
  }, []);

  function update(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  function updateCustom(key, v) {
    setCustomFields((c) => ({ ...c, [key]: v }));
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  async function submit(e) {
    e.preventDefault();
    setErr(null);
    setFieldErrors({});

    // Pre-flight required/type validation so errors show up immediately,
    // not after Firebase has already created the account.
    const pre = validateCustomFieldsClient(customDefs, customFields);
    if (!pre.ok) {
      setFieldErrors(pre.errorsByKey);
      setErr('Please fix the highlighted fields.');
      return;
    }
    const pwCheck = checkPassword(form.password);
    if (!pwCheck.ok) {
      setErr(`Password needs: ${pwCheck.failures.join(', ')}.`);
      return;
    }

    setBusy(true);
    try {
      const cred = await createUserWithEmailAndPassword(auth, form.email, form.password);
      if (form.displayName) await updateProfile(cred.user, { displayName: form.displayName });
      const { promotedToAdmin } = await Me.bootstrap({
        displayName: form.displayName,
        phone: form.phone,
        marketingOptIn: form.marketingOptIn,
        customFields,
      });
      try {
        await sendEmailVerification(cred.user, authActionSettings);
      } catch (verifyErr) {
        // eslint-disable-next-line no-console
        console.warn('verification email failed', verifyErr);
      }
      if (promotedToAdmin) await refreshRole();
      nav(promotedToAdmin ? '/admin' : '/profile');
    } catch (e) {
      if (e.data?.issues && Array.isArray(e.data.issues)) {
        const byKey = {};
        for (const it of e.data.issues) if (it.key) byKey[it.key] = it.message;
        setFieldErrors(byKey);
        setErr('Please fix the highlighted fields.');
      } else {
        setErr(e.message);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="py-10 md:py-16">
      <div className="max-w-md w-full mx-auto px-4">
        <div className="eyebrow text-brand mb-3">Members</div>
        <h1 className="font-display text-4xl mb-2">Create your account.</h1>
        <p className="text-ink-2 text-sm mb-8">Takes a minute. You can opt out of emails any time.</p>
        <form onSubmit={submit} className="space-y-4">
          <FormField label="Name" required>
            <input
              className="input"
              value={form.displayName}
              onChange={(e) => update('displayName', e.target.value)}
              required
            />
          </FormField>
          <FormField label="Email" required>
            <input
              type="email"
              className="input"
              value={form.email}
              onChange={(e) => update('email', e.target.value)}
              required
            />
          </FormField>
          <FormField label="Phone" hint="Optional — useful for training cancellations">
            <input className="input" value={form.phone} onChange={(e) => update('phone', e.target.value)} />
          </FormField>
          <FormField label="Password" hint={policySummary()} required>
            <input
              type="password"
              className="input"
              value={form.password}
              onChange={(e) => update('password', e.target.value)}
              required
              minLength={12}
            />
          </FormField>

          {customDefs.length > 0 && (
            <div className="pt-4 mt-2 border-t border-line space-y-4">
              {customDefs.map((f) => (
                <DynamicField
                  key={f.id}
                  field={f}
                  value={customFields[f.key]}
                  onChange={(v) => updateCustom(f.key, v)}
                  error={fieldErrors[f.key]}
                />
              ))}
            </div>
          )}

          <div className="pt-2">
            <Switch
              checked={form.marketingOptIn}
              onChange={(v) => update('marketingOptIn', v)}
              label="Club news & announcements"
              hint="Fixtures, socials, and the occasional fundraiser. Unsubscribe any time."
            />
          </div>
          {err && <p className="text-sm text-danger">{err}</p>}
          <button type="submit" className="btn-primary w-full h-11 mt-2" disabled={busy}>
            {busy ? 'Creating…' : 'Create account'}
          </button>
        </form>
        <p className="text-sm text-ink-2 mt-8">
          Already a member?{' '}
          <Link to="/login" className="text-brand underline underline-offset-4 font-medium">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
